import { WORDLIST } from './wordlist';

/**
 * Passphrase generation and key derivation for the cloud backup.
 *
 * The passphrase is the only thing between the blob in Azure and somebody reading this person's entire
 * utterance history. It is generated rather than chosen (see `wordlist.ts`), displayed once, and written
 * on a card — and if that card is lost, the cloud backups are gone for good. That is a deliberate trade
 * and the UI has to say it plainly rather than softening it.
 */

/** Words per passphrase. Six × 8 bits = 48 bits; read that together with the iteration count below. */
export const PASSPHRASE_WORDS = 6;

/**
 * PBKDF2 iterations — 600 000, the OWASP recommendation for PBKDF2-HMAC-SHA256.
 *
 * This is the number that makes 48 bits of passphrase defensible. An offline attacker holding the blob
 * faces 2⁴⁸ candidates × 600 000 HMAC operations each ≈ 1.7 × 10²⁰ operations; at 10¹⁰ hashes per second,
 * which is serious dedicated hardware, that is centuries. Weakening either number weakens both, so they
 * are documented together and neither should be changed without redoing the arithmetic.
 *
 * The cost on the device is paid twice in the lifetime of an install: once when the passphrase is minted
 * and once on a restore. Backups themselves are unattended and use the stored key, so nobody ever waits
 * for this while trying to speak. **Measure it on the iPad** — it is the one number here that a slow
 * A14 could make unpleasant, and the device checklist owns that.
 */
export const PBKDF2_ITERATIONS = 600_000;

export const SALT_BYTES = 16;

/**
 * A fresh passphrase.
 *
 * The wordlist is **exactly 256 long on purpose**: one random byte then maps onto one word with no
 * modulo bias and no rejection sampling. A 257th word would silently skew the distribution toward the
 * first entries, which is the kind of bug that never shows up in a test that only checks the format — so
 * the length is asserted here rather than trusted.
 */
export function generatePassphrase(): string {
  if (WORDLIST.length !== 256) {
    throw new Error(
      `wordlist must be exactly 256 words for unbiased selection, found ${WORDLIST.length}`,
    );
  }

  const bytes = crypto.getRandomValues(new Uint8Array(PASSPHRASE_WORDS));
  return [...bytes].map((byte) => WORDLIST[byte]).join(' ');
}

/**
 * What a typed passphrase becomes before it is used.
 *
 * Somebody reading six words off a card will capitalise one, double a space, or leave a trailing one.
 * None of that should mean "wrong passphrase" — the failure has to be reserved for actually wrong words,
 * or the message stops being informative at the worst possible moment.
 */
export function normalisePassphrase(input: string): string {
  return input.trim().toLowerCase().replace(/\s+/gu, ' ');
}

export function newSalt(): Uint8Array<ArrayBuffer> {
  return crypto.getRandomValues(new Uint8Array(SALT_BYTES));
}

/**
 * Derive the AES-GCM key.
 *
 * **Non-extractable.** The key is stored in IndexedDB so that daily backups need nobody present, and a
 * non-extractable `CryptoKey` survives being stored without the raw bytes ever being readable from
 * script. Anyone who can open the database can *use* it to seal a backup; they cannot lift the key out
 * and take it elsewhere.
 */
export async function deriveBackupKey(
  passphrase: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number = PBKDF2_ITERATIONS,
): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(normalisePassphrase(passphrase)),
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}
